/**
 * **시뮬레이션 화면이 받는 모양** — 엔진은 서버(`backend/src/sim`)에서만 돈다(사용자 지시 2026-10-11: 브라우저에서 돌면
 * 엔진 코드를 가져갈 수 있다). 여기에는 서버가 보내는 결과의 **타입과 화면에 필요한 이름 표**만 둔다.
 */

export type Element = 'Fire' | 'Water' | 'Wind' | 'Light' | 'Dark';

/** 로그 한 줄 */
export interface BattleLog {
    turn: number;
    actor: string;
    skill: string;
    target: string;
    damage: number;
    crit: boolean;
    glancing: boolean;
    note: string;
    hits?: number[];
}

/** 재생용 한 장면 — 몹 여섯(공덱 셋, 방덱 셋 순서)의 그때 상태와, 이 장면까지 쌓인 로그 줄 수 */
export interface BattleFrame {
    turn: number;
    /** 이번에 행동한 몹 (0~2 공덱, 3~5 방덱) — 처음 장면은 -1 */
    actor: number;
    units: Array<{ hp: number; maxHp: number; shield: number; atb: number; dead: boolean; fx: string[]; fxTurns?: number[] }>;
    logEnd: number;
}

/** 서버가 주는 재생 한 판 (`POST /api/sim/run` 의 replay) */
export interface Replay {
    win: boolean;
    timeout: boolean;
    turns: number;
    log: BattleLog[];
    frames: BattleFrame[];
}

/** 직접 플레이 — 고를 수 있는 스킬 하나 */
export interface MoveOption {
    slot: number;
    name: string;
    cooldown: number;
    usable: boolean;
    why?: string;
    aoe: boolean;
    utility: boolean;
    allyTarget: boolean;
    /** 적이든 아군이든 하나 (Justice 3스) */
    either?: boolean;
    hits: number;
    effects: string[];
}

/** 직접 플레이 — 지금 고를 차례 */
export interface Prompt {
    actor: number;
    options: MoveOption[];
    targets: number[];
    allies: number[];
    suggest: { slot: number; target: number } | null;
}

/** 직접 플레이 — 서버 세션 상태(로그·장면은 보낸 길이 뒤만 온다) */
export interface SessionView {
    id: string;
    log: BattleLog[];
    logFrom: number;
    frames: BattleFrame[];
    frameFrom: number;
    view: BattleFrame['units'];
    prompt: Prompt | null;
    result: { win: boolean; timeout: boolean; turns: number } | null;
    names: string[];
}

/** 룬 세트 번호 (게임의 set_id) */
export const SET = {
    활력: 1, 수호: 2, 신속: 3, 칼날: 4, 격노: 5, 집중: 6, 인내: 7, 맹공: 8,
    절망: 10, 흡혈: 11, 폭주: 13, 응보: 14, 의지: 15, 보호: 16, 반격: 17,
    파괴: 18, 투지: 19, 결의: 20, 강화: 21, 명중: 22, 저항: 23, 봉인: 24, 무형: 25
} as const;

/** 이로운 효과 이름 — 몹 위 효과 아이콘을 이로운 것 먼저 늘어놓는 데만 쓴다 */
export const BUFF_NAMES = new Set<string>([
    'Increase DEF', 'Increase ATK', 'Increase ATK SPD', 'Increase CRI Rate', 'Increase Damage', 'Increase CRI Resist', 'Immunity', 'Shield',
    'Endure', 'Invincible', 'Threat', 'Counter', 'Recovery', 'Defend', 'Accumulate ATK', 'Accumulate DEF', 'Decrease Damage',
    'Ignore Damage Reduction', 'Guaranteed Critical Hit', 'Debuff Bonus Damage', 'Stone'
]);
